import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL } from "./api/ApiUrls";
import { logOut } from "../store/reducers/auth";
import { useLocation } from "react-router-dom";

export interface Customer {
  id: number;
  customerName: string;
  age: number;
  sex: string;
  spouseName: string;
  spouseAge: number;
  spouseSex: string;
  yearsMarried: string;
  source: string;
  status: boolean;
  customerStatus: string;
  serviceChoosen: string;
  wellnessCategory: string;
  wellnessTips: any;
  leadType: any;
  referredBy: string;
  occupation: string;
  dob: any;
}

type CustomersResponse = Customer[];

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    headers.set("hospitalCode", localStorage.getItem("hospitalCode"));
    const token = (getState() as any).auth.token;
    if (token) {
      // console.log(getState);

      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // try to get a new token
    api.dispatch(logOut());
  }
  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Customer",
    "DasboardCustomers",
    "Branch",
    "PotentialLeads",
    "DashBoardCount",
    "DashBoardLeads",
    "CustomerReminder",
    "PermissionManagement",
    "Permissions",
    "Modules",
    "RoleManagement",
    "UserManagement",
    "Reports",
    "Treatment",
    "Hosp",
    "BloodTests",
    "TestReports",
    "Prescription",
  ],
  endpoints: (build) => ({
    getCustomers: build.query<CustomersResponse, void>({
      query: () => "customer",
      providesTags: (result) =>
        result
          ? [
              ...result["data"]?.map(({ id }) => ({
                type: "Customer" as const,
                id,
              })),
              { type: "Customer", id: "LIST" },
            ]
          : [{ type: "Customer", id: "LIST" }],
    }),
    addCustomer: build.mutation<Customer, Partial<Customer>>({
      query: (body) => ({
        url: `customer`,
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: Customer }, meta, arg) =>
        response.data,
      invalidatesTags: [
        { type: "Customer", id: "LIST" },
        { type: "DasboardCustomers", id: "LIST" },
        { type: "DashBoardCount" },
      ],
    }),
    addBulkCustomer: build.mutation<any, FormData>({
      query: (formData) => ({
        url: `customer_ivf_bulk_upload`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        { type: "Customer", id: "LIST" },
        { type: "DasboardCustomers", id: "LIST" },
        { type: "DashBoardCount" },
      ],
    }),
    
    getCustomer: build.query<Customer, string>({
      query: (id) => `customer/${id}`,
      transformResponse: (response: { data: Customer }, meta, arg) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "Customer", id }],
    }),
    updateCustomer: build.mutation<
      void,
      Pick<Customer, "id"> & Partial<Customer>
    >({
      query: ({ id, ...patch }) => ({
        url: `customer/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Customer", id },
        { type: "DasboardCustomers", id: "LIST" },
        { type: "DashBoardCount" },
      ],
    }),
    deleteCustomer: build.mutation<{ success: boolean; id: number }, number>({
      query(id) {
        return {
          url: `customer/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, id) => [
        { type: "Customer", id },
        { type: "DasboardCustomers", id: "LIST" },
      ],
    }),
    getCustomerViewDetails: build.query<any, string>({
      query: (id) => `customer_view/${id}`,
      transformResponse: (response: { data: any }, meta, arg) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "Customer", id }],
    }),
  }),
});

export const {
  useGetCustomerQuery,
  useGetCustomersQuery,
  useAddCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useAddBulkCustomerMutation,
  useGetCustomerViewDetailsQuery
} = api;
